// ══════════════════════════════════════════════════════════════
// COMPASS – Management Company Effects
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ManagementCompanyActions } from './management-company.actions';
import { ToastActions } from '../toast/toast.actions';
import { UserActions } from '../user/user.actions';
import { ApiConfiguration } from '../../api/api-configuration';
import { getAll } from '../../api/fn/management-company/get-all';
import { getPermissions } from '../../api/fn/management-company/get-permissions';
import { ManagementCompanyDto } from '../../api/models/management-company-dto';
import { convertApiPermissions, Permission } from '../../models/permission.config';
import { UserRole } from '../../models/user-role.enum';
import { FundActions } from '../fund/fund.actions';
import { ProcessActions } from '../process/process.actions';
import { BoardMemberActions } from '../board-member/board-member.actions';
import { selectSelectedCompanyId } from './management-company.selectors';
import { errorMessage, readApiError } from '../../utils/api-error.utils';

@Injectable()
export class ManagementCompanyEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private firstPermissionsLoad = true;

  readonly loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementCompanyActions.loadCompanies),
      switchMap(() =>
        getAll(this.http, this.apiConfig.rootUrl).pipe(
          map((response) => {
            const body = response.body;
            const companies: ManagementCompanyDto[] = Array.isArray(body) ? body : [body];
            return ManagementCompanyActions.loadCompaniesSuccess({ companies });
          }),
          catchError((error) =>
            [ManagementCompanyActions.loadCompaniesFailure({ error: errorMessage(readApiError(error), 'COMPANY.ERROR.LOAD_COMPANIES') })]
          )
        )
      )
    )
  );

  readonly loadCompaniesFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementCompanyActions.loadCompaniesFailure),
      map(({ error }) =>
        ToastActions.showToast({
          message: error,
          notificationType: 'error',
        })
      )
    )
  );

  /** After companies load, trigger permission load for the first (auto-selected) company */
  readonly autoLoadPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementCompanyActions.loadCompaniesSuccess),
      withLatestFrom(this.store.select(selectSelectedCompanyId)),
      filter(([, companyId]) => !!companyId),
      map(([, companyId]) =>
        ManagementCompanyActions.loadPermissions({ companyId: companyId! })
      )
    )
  );

  /** When user switches company, clear feature stores and reload permissions */
  readonly onCompanySelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementCompanyActions.selectCompany),
      switchMap(({ companyId }) => [
        // Clear all feature stores so stale data from the previous company doesn't show
        FundActions.clearFunds(),
        ProcessActions.clearProcesses(),
        BoardMemberActions.clearBoardMembers(),

        // Reload permissions for the new company
        ManagementCompanyActions.loadPermissions({ companyId }),
      ])
    )
  );

  /** Call the permissions API */
  readonly loadPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementCompanyActions.loadPermissions),
      switchMap(({ companyId }) =>
        getPermissions(this.http, this.apiConfig.rootUrl, { id: companyId }).pipe(
          map((response) =>
            ManagementCompanyActions.loadPermissionsSuccess({ permissionDto: response.body })
          ),
          catchError((error) =>
            [ManagementCompanyActions.loadPermissionsFailure({
              error: errorMessage(readApiError(error), 'COMPANY.ERROR.LOAD_PERMISSIONS'),
            })]
          )
        )
      )
    )
  );

  /** On permission load success, push roles + permissions into user store */
  readonly setUserPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementCompanyActions.loadPermissionsSuccess),
      map(({ permissionDto }) => {
        const roles = (permissionDto.roles ?? [])
          .filter((r): r is UserRole => Object.values(UserRole).includes(r as UserRole))
          .map((r) => r as UserRole);
        const permissions = convertApiPermissions(permissionDto.permissions);
        return UserActions.setCompanyPermissions({ roles, permissions });
      })
    )
  );

  /** On first permissions load, redirect if on a generic entry point.
   *  Uses permissions only — no role checks. */
  readonly redirectAfterPermissionsLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ManagementCompanyActions.loadPermissionsSuccess),
        filter(() => {
          if (this.firstPermissionsLoad) {
            this.firstPermissionsLoad = false;
            return true;
          }
          return false;
        }),
        withLatestFrom(this.store.select(selectSelectedCompanyId)),
        map(([{ permissionDto }, companyId]) => {
          const currentUrl = new URL(window.location.href).pathname;
          const isGenericEntry = currentUrl === '/' || currentUrl.startsWith('/auth');
          if (!isGenericEntry || !companyId) return;

          const permissions = convertApiPermissions(permissionDto.permissions);

          // Find first accessible section based on permissions
          if (permissions.includes(Permission.PROCESS_READ)) {
            this.router.navigate(['/company', companyId, 'processes']);
          } else if (permissions.includes(Permission.FUND_READ)) {
            this.router.navigate(['/company', companyId, 'funds']);
          } else if (permissions.length === 0) {
            // No permissions at all — show contact admin page
            this.router.navigate(['/error'], {
              queryParams: { type: 'no-permissions' },
            });
          } else {
            this.router.navigate(['/company', companyId]);
          }
        })
      ),
    { dispatch: false }
  );

  readonly loadPermissionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagementCompanyActions.loadPermissionsFailure),
      map(({ error }) =>
        ToastActions.showToast({
          message: error,
          notificationType: 'error',
        })
      )
    )
  );
}
